import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, MailMinusIcon, HardHatIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { FrameworkWheel } from '../components/FrameworkWheel';
interface HeroProps {
  onDownload: () => void;
}
const PROOF = [{
  Icon: HardHatIcon,
  text: 'Built by a working mine geologist'
}, {
  Icon: MailMinusIcon,
  text: 'Field notes sent only when genuinely useful'
}, {
  Icon: ShieldCheckIcon,
  text: 'No spam. Unsubscribe anytime.'
}];
export function Hero({
  onDownload
}: HeroProps) {
  return <section id="top" className="relative w-full overflow-hidden bg-cream" data-id="element-565">
      <div className="mgs-grid absolute inset-0" aria-hidden data-id="element-566" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-14 lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8 lg:py-24" data-id="element-567">
        <motion.div className="lg:col-span-6" initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-568">
          <p className="inline-flex items-center gap-2 rounded-full border border-charcoal/15 bg-sand px-3 py-1 text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-569">
            Better Systems – Better Decisions – Better Outcomes
          </p>

          <p className="mt-6 border-l-2 border-yellow pl-4 text-lg font-medium italic leading-snug text-charcoal/80 sm:text-xl" data-id="element-570">
            Most mine geology problems are not isolated technical issues. They
            are failures in how the system operates.
          </p>

          <h1 className="mt-5 text-4xl font-black leading-[1.05] tracking-tight text-charcoal sm:text-5xl lg:text-[3.3rem]" data-id="element-571">
            Mine Geology Systems: a practical framework for clearer geological
            decisions
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-warmgray" data-id="element-572">
            Mine Geology Systems (MGS) is a way of thinking about mine geology
            as a connected operational system underground. It is an evolving
            framework focused on operational learning and continuous improvement
            in mine geology systems.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-573">
            <Button variant="primary" onClick={onDownload} data-id="element-574">
              Download the Free MGS Toolkit
            </Button>
            <Button variant="secondary" to="/framework" data-id="element-575">
              Explore the system
            </Button>
          </div>

          <ul className="mt-8 flex flex-col gap-2.5 border-t border-charcoal/10 pt-6 sm:flex-row sm:flex-wrap sm:gap-x-6" data-id="element-576">
            {PROOF.map(({
            Icon,
            text
          }) => <li key={text} className="inline-flex items-center gap-2 text-sm font-medium text-charcoal/65" data-id="element-577">
                <Icon className="h-4 w-4 text-yellow" strokeWidth={2.25} data-id="element-578" />
                {text}
              </li>)}
          </ul>
        </motion.div>

        <motion.div className="lg:col-span-6" initial={{
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: 'easeOut'
      }} data-id="element-579">
          <div className="relative mx-auto max-w-[560px]" data-id="element-580">
            <div className="mgs-traces absolute -left-4 -top-4 h-20 w-20 rounded-tl-2xl opacity-70" aria-hidden data-id="element-581" />
            <div className="mgs-traces absolute -bottom-4 -right-4 h-20 w-20 rounded-br-2xl opacity-70" aria-hidden data-id="element-582" />
            <div className="relative rounded-2xl border border-charcoal/10 bg-cream/80 p-5 shadow-card backdrop-blur-sm sm:p-7" data-id="element-583">
              <FrameworkWheel data-id="element-584" />
              <p className="mt-4 text-center text-xs font-medium uppercase tracking-widest text-warmgray" data-id="element-585">
                The MGS framework — hover any part to explore
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}